/**
 * Message composer — adapted from aurora's chat ContentFooter/TextInput
 * (Enter sends, Shift+Enter breaks, multiline maxRows 3), reduced to the
 * single text field the assistant needs. Disabled while a reply streams
 * (first line of defense — the BE also 409s a concurrent send).
 */

import { KeyboardEvent, useEffect, useState } from 'react';
import { IconButton, InputAdornment, TextField, alpha } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import IconifyIcon from 'components/base/IconifyIcon';

interface AssistantComposerProps {
  disabled: boolean;
  onSend: (content: string) => void;
  /** Set after a pre-stream failure — restores the rolled-back message text. */
  restoredDraft: string | null;
  /**
   * Visual scale. `md` (default) is the drawer's compact field; `lg` is the
   * larger, rounded, elevated pill used by the full-screen planner page.
   */
  size?: 'md' | 'lg';
}

// Larger, softly-elevated white pill for the focus page — a distinct, calmer
// input than the drawer's compact field. Rendered as an OUTLINED field (the
// app's default TextField is `filled`, which greys the background and darkens
// it further on hover): here we hold a clean white surface with only a subtle
// border, so there is no grey fill and no grey hover — the border stays steady
// and just the focus ring changes.
//
// A function of the theme so the elevation adapts to light/dark: a soft,
// diffuse drop shadow (not MUI's hard `boxShadow: 3`) that deepens with a
// faint brand tint on focus — a premium, Gemini-like resting surface.
const lgFieldSx = (theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';
  const restShadow = isDark
    ? `0 8px 30px ${alpha(theme.palette.common.black, 0.5)}`
    : `0 10px 34px ${alpha('#0f172a', 0.08)}, 0 2px 8px ${alpha('#0f172a', 0.05)}`;
  const focusShadow = isDark
    ? `0 10px 34px ${alpha(theme.palette.common.black, 0.6)}`
    : `0 12px 40px ${alpha(theme.palette.primary.main, 0.16)}`;

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3.5,
      bgcolor: 'background.paper',
      fontSize: '1.05rem',
      px: 1.5,
      py: 1,
      boxShadow: restShadow,
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'divider',
      },
      '&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
        borderColor: 'divider',
      },
      '&.Mui-focused': {
        boxShadow: focusShadow,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
      },
    },
  };
};

// The focus page's send affordance: a filled, circular primary button (Gemini-
// like) that quietly greys out when there's nothing to send. The drawer's
// compact `md` field keeps its bare icon button.
const lgSendSx = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  width: 40,
  height: 40,
  transition: 'background-color 0.2s ease, transform 0.1s ease',
  '&:hover': { bgcolor: 'primary.dark' },
  '&:active': { transform: 'scale(0.94)' },
  '&.Mui-disabled': {
    bgcolor: 'action.disabledBackground',
    color: 'action.disabled',
  },
};

const AssistantComposer = ({
  disabled,
  onSend,
  restoredDraft,
  size = 'md',
}: AssistantComposerProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const isLg = size === 'lg';

  useEffect(() => {
    if (restoredDraft) setValue((prev) => prev || restoredDraft);
  }, [restoredDraft]);

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    setValue('');
    onSend(text);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <TextField
      fullWidth
      multiline
      maxRows={isLg ? 5 : 3}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={t('assistant.composer.placeholder')}
      variant={isLg ? 'outlined' : undefined}
      sx={isLg ? lgFieldSx : undefined}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                color="primary"
                onClick={submit}
                disabled={disabled || !value.trim()}
                aria-label={t('assistant.composer.send_aria')}
                sx={isLg ? lgSendSx : undefined}
              >
                <IconifyIcon icon="material-symbols:send-rounded" fontSize={20} />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

export default AssistantComposer;
